import {useFocusEffect} from '@react-navigation/native';
import {StackScreenProps} from '@react-navigation/stack';
import {Box, Text} from 'native-base';
import React, {useCallback, useState} from 'react';
import {FlatList} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {AccountStatus, TransactionFieldsFragment, useTransactionsQuery} from '../api/generated';
import {LoadingEntriesSkeleton} from '../components/LoadingEntriesSkeleton';
import {PaymentItem} from '../components/PaymentItem';
import {useAsyncAction} from '../hooks/useAsyncAction';
import i18n from '../lib/i18n';
import {Nav, NavParams} from './navigation';
import {useAccountContext} from '../lib/accountContext';

const size = 20;

export const PaymentsScreen: React.FC<StackScreenProps<NavParams, Nav.PaymentsScreen>> = ({
  navigation
}) => {
  const [firstFocus, setFirstFocus] = useState(false);
  const account = useAccountContext();
  const {
    data,
    refetch,
    loading: loadingPayments,
    fetchMore
  } = useTransactionsQuery({
    skip: account?.status === AccountStatus.INACTIVE,
    variables: {
      size,
      from: 0
    }
  });

  const [onRefresh, isRefreshing] = useAsyncAction(refetch);
  const payments = data?.transactions.items || [];

  useFocusEffect(
    useCallback(() => {
      if (!firstFocus) {
        setFirstFocus(true);
      } else {
        refetch();
      }
    }, [refetch])
  );

  const handleLoadMore = async () => {
    return fetchMore({variables: {from: payments.length}});
  };

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <Box style={[t.flex1, t.mT2]}>
        <LoadingEntriesSkeleton isLoading={loadingPayments} numberOfEntries={size}>
          <FlatList<TransactionFieldsFragment>
            data={payments}
            onRefresh={onRefresh}
            refreshing={isRefreshing}
            onEndReachedThreshold={0.5}
            initialNumToRender={size}
            ListEmptyComponent={() => (
              <Text style={[t.textGray600, t.textCenter, t.pY6, t.pX4, t.flex1]}>
                {i18n.t('noPayments')}
              </Text>
            )}
            onEndReached={handleLoadMore}
            renderItem={({item}) => (
              <PaymentItem
                item={item}
                key={item.id}
                onPress={() => navigation.navigate(Nav.PaymentScreen, {paymentId: item.id})}
              />
            )}
            keyExtractor={(item) => item.id}
          />
        </LoadingEntriesSkeleton>
      </Box>
    </SafeAreaView>
  );
};
